<template>
    <div class="a-video-card" :class="className">
        <a-youtube v-if="shouldEmbed" :yt-id="ytId" />
        <component
            :is="to ? 'dynamic-link' : 'div'"
            v-else
            v-modal="modalTriggerId"
            v-bind="linkProps"
            class="a-video-card__wrapper"
            @click="onPreviewClick"
        >
            <a-picture
                class="a-video-card__img"
                :link="image"
                :alt="imageAlt || text"
                :fit="imageFit"
                is-background
            />
            <div v-if="cta" class="a-video-card__cta">
                <a-button
                    class="a-video-card__cta-button"
                    v-bind="resolvedCta"
                    @click.stop.prevent="onCtaClick"
                />
            </div>
            <div v-else :class="classNameGlyph">
                <a-glyph :name="glyph" />
            </div>
            <div class="a-video-card__content">
                <v-clamp class="a-video-card__text" autoresize :max-lines="1">
                    {{ text }}
                </v-clamp>
                <span v-if="time" class="a-video-card__time">
                    {{ time }}
                </span>
            </div>
        </component>
        <a-modal
            v-if="shouldShowModal"
            :id="modalId"
            type="youtube"
            is-youtube
        >
            <a-youtube
                :yt-id="ytId"
                :params="ytParams"
                :title="text"
                :autoplay-on-open="autoplayOnOpen"
                @progressChanged="$emit('progressChanged', $event)"
            />
        </a-modal>
    </div>
</template>

<script>
import VClamp from 'vue3-clamp';
import AButton from '@core/components/button/button.vue';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import AModal from '@core/components/modal/modal.vue';
import APicture from '@core/components/picture/picture.vue';
import AYoutube from '@core/components/youtube/youtube.vue';
import Modal from '@core/directives/modal.js';
import breakpoint from '@core/mixins/breakpoint.js';
import {
    ONETRUST_CONSENT_CHANGED,
    ONETRUST_NATIVE_CONSENT_CHANGED,
    ONETRUST_TARGETING_COOKIES_GROUP,
} from '@model/const/onetrust.ts';
import { SIZES } from './constants.js';

export default {
    name: 'AVideoCard',

    components: {
        AYoutube,
        VClamp,
        AButton,
        AGlyph,
        AModal,
        APicture,
        DynamicLink,
    },

    directives: {
        Modal,
    },

    mixins: [breakpoint],

    props: {
        /**
         * Youtube id
         */
        ytId: {
            type: String,
            default: null,
        },

        to: {
            type: String,
            default: null,
        },

        target: {
            type: String,
            default: undefined,
        },

        rel: {
            type: String,
            default: undefined,
        },

        title: {
            type: String,
            default: undefined,
        },

        ariaLabel: {
            type: String,
            default: undefined,
        },

        event: {
            type: Object,
            default: () => ({}),
        },

        utmWebteam: {
            type: String,
            default: null,
        },

        ytParams: {
            type: Object,
            default: () => ({ autoplay: 1 }),
        },

        autoplayOnOpen: {
            type: Boolean,
            default: true,
        },

        /**
         * Text
         */
        text: {
            type: String,
            default: undefined,
        },

        /**
         * Video time
         */
        time: {
            type: String,
            default: undefined,
        },

        /**
         * Image URL
         */
        image: {
            type: String,
            required: true,
        },

        /**
         * Image alt
         */
        imageAlt: {
            type: String,
            default: undefined,
        },

        /**
         * Size
         */
        size: {
            type: String,
            default: undefined,
            validator: (value) => value === undefined || SIZES.includes(value),
        },

        glyph: {
            type: String,
            default: 'play',
        },
        isEmbeded: {
            type: Boolean,
            default: false,
        },
        cta: {
            type: Object,
            default: undefined,
        },
        imageFit: {
            type: String,
            default: 'fill',
        },
    },

    emits: ['progressChanged'],

    data: () => ({
        oneTrustConsentWasGiven: false,
    }),

    computed: {
        modalId() {
            return this.ytId ? `yt_id-${this.ytId}` : null;
        },
        modalTriggerId() {
            return !this.to && this.ytId ? this.modalId : null;
        },
        shouldEmbed() {
            return Boolean(!this.to && this.ytId && this.isEmbeded && this.oneTrustConsentWasGiven);
        },
        shouldShowModal() {
            return Boolean(!this.to && this.ytId && !this.isEmbeded && this.oneTrustConsentWasGiven);
        },
        linkProps() {
            if (!this.to) return {};

            return {
                to: this.to,
                target: this.target,
                rel: this.rel,
                title: this.title || this.text,
                ariaLabel: this.ariaLabel || this.text,
                event: this.event,
                utmWebteam: this.utmWebteam,
                nativeType: 'a',
            };
        },
        computedSize() {
            if (this.size) return this.size;
            return this.isMobile ? 'small' : 'large';
        },
        className() {
            const { computedSize } = this;
            return {
                [`a-video-card_size_${computedSize}`]: computedSize,
                'a-video-card--no-overlay': !this.text,
            };
        },
        classNameGlyph() {
            return [
                'a-video-card__glyph',
                [`a-video-card__glyph-${this.glyph}`],
            ];
        },
        youtubeUrl() {
            return `https://www.youtube.com/watch?v=${this.ytId}`;
        },
        resolvedCta() {
            if (!this.cta) return null;

            // CTA in video-card replaces only visual play control.
            // Navigation/open behavior must stay the same as video click/play.
            return {
                ...this.cta,
                to: null,
                target: undefined,
                nativeType: 'button',
            };
        },
    },

    mounted() {
        this.checkOneTrustConsent();
        window.addEventListener(ONETRUST_CONSENT_CHANGED, this.checkOneTrustConsent);
        window.addEventListener(ONETRUST_NATIVE_CONSENT_CHANGED, this.checkOneTrustConsent);
    },

    beforeUnmount() {
        window.removeEventListener(ONETRUST_CONSENT_CHANGED, this.checkOneTrustConsent);
        window.removeEventListener(ONETRUST_NATIVE_CONSENT_CHANGED, this.checkOneTrustConsent);
    },

    methods: {
        checkOneTrustConsent() {
            if (typeof window.OptanonActiveGroups === 'undefined') return;
            this.oneTrustConsentWasGiven = window.OptanonActiveGroups.includes(ONETRUST_TARGETING_COOKIES_GROUP);
        },

        openYouTubePage() {
            if (!this.ytId) return;
            window.open(this.youtubeUrl, '_blank');
        },
        onPreviewClick() {
            if (this.to || this.oneTrustConsentWasGiven) return;
            this.openYouTubePage();
        },
        onCtaClick() {
            if (this.to) {
                this.$el?.querySelector('.a-video-card__wrapper')?.click();
                return;
            }

            if (!this.oneTrustConsentWasGiven) {
                this.openYouTubePage();
                return;
            }

            const trigger = this.$el?.querySelector('.a-video-card__wrapper');
            trigger?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        },
    },
};
</script>

<style lang="pcss">
.a-video-card {
    width: 100%;
    aspect-ratio: 16 / 9;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    border-radius: 4px;
    border: 1px solid var(--av-brand-secondary-light, rgba(64, 139, 234, 0.3));
    box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
    transition: all 0.2s ease-in-out;

    &_size {
        &_small {
            .a-video-card__glyph {
                padding: 16px;
                width: 56px;
                height: 56px;

                .a-glyph {
                    width: 24px;
                    height: 24px;
                }
            }
            .a-video-card__text {
                @mixin caption;
            }
        }

        &_large {
            .a-video-card__glyph {
                padding: 20px;
                width: 72px;
                height: 72px;

                .a-glyph {
                    width: 32px;
                    height: 32px;
                }
            }
            .a-video-card__text {
                @mixin paragraph;
            }
        }
    }

    &__wrapper {
        position: absolute;
        top: 0;
        inset-inline-start: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        &::before {
            content: '';
            position: absolute;
            border: var(--av-border);
            border-radius: 4px;
            top: 0;
            inset-inline-start: 0;
            bottom: 0;
            inset-inline-end: 0;
            z-index: 6;
        }
    }

    &__img {
        position: absolute;
        top: 0;
        inset-inline-start: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        transition: transform 0.2s ease-in-out;

        &:before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(192deg, rgba(0, 49, 127, 0) 48.94%, rgba(0, 49, 127, 0.8) 100%);
            z-index: 1;
            opacity: 1;
            pointer-events: none;
            will-change: opacity;
            transition: opacity 0.2s ease-in-out;
        }

        &:after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(192deg, rgba(0, 91, 204, 0) 48.94%, #005BCC 100%);
            z-index: 2;
            opacity: 0;
            pointer-events: none;
            will-change: opacity;
            transition: opacity 0.2s ease-in-out;
        }
    }

    &:hover,
    &:active {
        box-shadow: none;
        transition: box-shadow 0.2s ease-in-out;

        .a-video-card {
            &__img {
                transform: scale(1.02);

                &:before {
                    opacity: 0;
                }

                &:after {
                    opacity: 1;
                }
            }

            &__glyph-play {
                background-color: #0065e3;
                box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);

                &:hover {
                    background-color: #005bcc;
                }
            }

            &__glyph-play-gradient {
                background-color: var(--av-inversed-primary);

                &:hover {
                    background-color: var(--av-inversed-primary);
                }
            }
        }
    }

    &:active {
        .a-video-card__glyph-play {
            background-color: #0051b6;

            &:active {
                background-color: #0051b6;
            }
        }
    }

    &__content {
        position: absolute;
        z-index: 1;
        bottom: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
        width: 100%;
        padding: 0 8px 8px 16px;
        height: 80%;
        display: flex;
        align-items: flex-end;
    }

    &__glyph {
        position: absolute;
        width: 56px;
        height: 56px;
        border-radius: 50px;
        display: flex;
        transition: all ease-in-out 0.2s;
        flex-shrink: 0;
        z-index: 5;

        .a-glyph {
            fill: var(--av-inversed-primary);
            margin: auto;
            width: 24px;
            height: 24px;
        }
    }

    &__glyph-play {
        background: #0065e3;
    }

    &__glyph-play-gradient {
        background: var(--av-inversed-primary);
    }

    &__cta {
        position: absolute;
        z-index: 5;
    }

    &__cta-button {
        transition: box-shadow ease-in-out 0.2s;
    }

    &__text {
        @mixin caption;
        color: var(--av-inversed-primary);
        font-weight: 600;
        padding-inline-end: 24px;
        margin-bottom: 8px;
        flex: 1;
    }

    &__time {
        @mixin caption;
        color: var(--av-inversed-primary);
        background: var(--av-fixed-lighter);
        border-radius: 4px;
        padding: 0 2px;
    }

    &--no-overlay {
        .a-video-card__img {
            &:before,
            &:after {
                display: none;
            }
        }
    }
}
</style>
